import { ChevronDown as ExpandMore, ChevronUp as ExpandLess, X as Close } from "lucide-react";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import PriceSlider from "./PriceSlider";
import { academyLogger } from "utils/logger";

const FilterPanel = ({
  categories,
  selectedCategories,
  handleCategoryChange,
  maxPrice,
  selectedPriceRange,
  handlePriceChange,
  sortingOrder,
  toggleSortingOrder,
  handleClearFilters,
  setFilterVisible,
  filteredProducts,
}) => {
  const [priceCollapsed, setPriceCollapsed] = useState(false);
  const user = useSelector((state) => state?.auth?.data);
  const [selectedAttributes, setSelectedAttributes] = useState({}); // To track selected attributes
  const handlePriceCollapse = () => setPriceCollapsed(!priceCollapsed);

  academyLogger.info("Filtered Products in FilterPanel:", filteredProducts);

  const optionalAttributes = (filteredProducts || []).flatMap(
    (product) => product.optionalAttributes || []
  );

  // Group the attributes
  const groupedAttributes = optionalAttributes.reduce((acc, { name, values }) => {
    const normalizedName = name.toLowerCase();
    const processedValues = values.map((v) =>
      typeof v === "string" ? v.toLowerCase() : v.value.toLowerCase()
    );
    const existingAttribute = acc.find(
      (attr) => attr.name.toLowerCase() === normalizedName
    );

    if (existingAttribute) {
      existingAttribute.values = [
        ...new Set([...existingAttribute.values, ...processedValues]),
      ];
    } else {
      acc.push({ name, values: processedValues });
    }

    return acc;
  }, []);

  academyLogger.info("Grouped Attributes:", groupedAttributes);

  const handleButtonClick = (attributeName, value) => {
    const newSelectedAttributes = { ...selectedAttributes };
    if (newSelectedAttributes[attributeName] === value) {
      delete newSelectedAttributes[attributeName]; // Deselect if already selected
    } else {
      newSelectedAttributes[attributeName] = value;
    }
    setSelectedAttributes(newSelectedAttributes); // Update the selected attributes

    // Filter products based on the selected attribute and value
    const filtered = filteredProducts.filter((product) => {
      const matchingAttribute = product.optionalAttributes.find(
        (attr) => attr.name.toLowerCase() === attributeName.toLowerCase()
      );

      if (matchingAttribute) {
        return matchingAttribute.values.some((v) => {
          const valueToCheck = typeof v === "string" ? v : v.value;
          return valueToCheck === value;
        });
      }

      return false;
    });

    academyLogger.info("Filtered Products Based on Selected Attributes:");
    academyLogger.info(filtered);
  };

  return (
    <div className="p-6 h-full flex flex-col relative bg-white shadow-lg rounded-lg transition-all duration-300">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <span className="ml-2 font-bold text-gray-800 text-lg">
            Hello, {user?.firstName || "Please sign in"}
          </span>
        </div>
        <button
          onClick={() => setFilterVisible(false)}
          className="text-gray-600 hover:text-[#B88E2F] transition-transform duration-200 hover:scale-110"
        >
          <Close />
        </button>
      </div>

      <div className="filter-body flex-grow overflow-y-auto">
        <div className="grid grid-cols-1 gap-6">

          {/* Price Filter Section */}
          <div className="w-full">
            <button
              onClick={handlePriceCollapse}
              className={`mb-2 w-full border ${priceCollapsed ? "bg-gray-100" : "bg-[#B88E2F]"
                } text-white rounded-lg p-3 shadow transition-colors duration-300 flex items-center justify-between`}
            >
              <span className="font-semibold">Price</span>
              {priceCollapsed ? <ExpandMore /> : <ExpandLess />}
            </button>
            {!priceCollapsed && (
              <div className="mt-4">
                <span className="font-bold text-gray-800 mb-2 block">
                  Price Range
                </span>
                <PriceSlider
                  maxPrice={maxPrice}
                  selectedPriceRange={selectedPriceRange}
                  handlePriceChange={handlePriceChange}
                  currencySymbol="$"
                  className="mt-1"
                />
              </div>
            )}
          </div>

          {/* Optional Attributes Section */}
          {groupedAttributes.length > 0 && (
            <div className="w-full">
              <fieldset className="border border-gray-200 p-4 rounded-lg">
                <legend className="font-bold text-gray-800 px-2">
                  More Options
                </legend>
                <div className="space-y-4">
                  {groupedAttributes.map((attribute, index) => (
                    <div key={index} className="flex flex-col">
                      <span className="font-semibold text-gray-700 mb-2">
                        {attribute.name}
                      </span>
                      <div className="flex flex-wrap gap-2">
                        {attribute.values.map((value, i) => (
                          <button
                            key={i}
                            onClick={() => handleButtonClick(attribute.name, value)}
                            className={`border-2 rounded-lg px-3 py-1 text-sm font-semibold transition-colors duration-300
                                ${selectedAttributes[attribute.name] === value
                                ? "bg-[#B88E2F] text-white border-[#B88E2F]"
                                : "bg-transparent text-gray-700 border-gray-300 hover:bg-[#B88E2F] hover:text-white hover:border-[#B88E2F]"
                              }`}
                          >
                            {value}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </fieldset>
            </div>
          )}

          {/* Clear Filters Button */}
          <button
            onClick={handleClearFilters}
            className="bg-[#B88E2F] text-white rounded-lg py-3 shadow mt-4 w-full hover:bg-[#A67C2A] transition-colors duration-200 font-semibold"
          >
            Clear All Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;
